// externals
import { forwardRef } from 'react';

// libraries
import { NumericInput as InterstateNumericInput } from '@interstate/components/NumericInput';
import type { NumericInputProps } from '@interstate/components/NumericInput';
import { useSelectOnFocusEnabledStrict, useSelectOnFocusEnabledWithLegacyFallback } from '../selectOnFocus/selectOnFocus';
import type { SelectOnFocusSelector } from '../selectOnFocus/selectOnFocus';
import { EnhancedNumericInput } from './EnhancedNumericInput';

type Props = NumericInputProps & { selectOnFocusSelector?: SelectOnFocusSelector; strictSelectOnFocus?: boolean };

/**
 * Gate for the whole dr-dash-scoped experience.
 * Off => an untouched, thin Interstate pass-through, identical to pre-DE753030 behavior.
 * On => the rebuilt masking/focus behavior in EnhancedNumericInput`.
 */
export const NumericInput = forwardRef<unknown, Props>(
    ({ selectOnFocusSelector, strictSelectOnFocus = false, ...props }, forwardedRef) => {
        const isLegacyFallbackEnabled = useSelectOnFocusEnabledWithLegacyFallback(selectOnFocusSelector);
        const isStrictEnabled = useSelectOnFocusEnabledStrict(selectOnFocusSelector);
        const isAutoSelectEnabled = strictSelectOnFocus ? isStrictEnabled : isLegacyFallbackEnabled;

        if (!isAutoSelectEnabled) {
            return <InterstateNumericInput {...(props as NumericInputProps)} ref={forwardedRef} />;
        }

        return <EnhancedNumericInput {...props} ref={forwardedRef} />;
    }
);

NumericInput.displayName = 'NumericInput';
